import { Injectable } from '@angular/core';
import Pusher, { Channel } from 'pusher-js';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MessageModel } from '../models/message.model';
import * as fromApp from "../store/app.reducer";
import { map, take } from 'rxjs/operators';

export interface AssignmentUpdateEvent {
    assignment_id: number;
    action: string;
    status: string;
}

@Injectable({
    providedIn: 'root'
})
export class PusherService {
    private pusher: Pusher;
    private activeChannels: Map<number, Channel> = new Map();
    private processedMessageIds: Set<number> = new Set();

    private assignmentChannels: Map<number, Channel> = new Map();
    private desiredAssignmentCustomerIds: Set<number> = new Set();
    private assignmentUpdateSubject = new Subject<AssignmentUpdateEvent>();
    readonly assignmentUpdates$ = this.assignmentUpdateSubject.asObservable();

    // Reconnection management
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private baseReconnectDelay = 1000; // 1 second
    private maxReconnectDelay = 30000; // 30 seconds
    private reconnectTimer: any;
    private isReconnecting = false;

    constructor(private store: Store<fromApp.AppState>) {
        this.initializePusher();
    }

    private getAuthToken(): string | null {
        return localStorage.getItem('jwt');
    }

    private ensureInitialized(): boolean {
        if (this.pusher) {
            return true;
        }
        this.initializePusher();
        return !!this.pusher;
    }

    public initializePusher() {
        try {
            const token = this.getAuthToken();
            if (!token) {
                // Logged-out users do not have a private-channel token; skip initialization quietly.
                return;
            }

            // Disconnect existing connection if any
            if (this.pusher) {
                this.disconnect();
            }

            this.pusher = new Pusher(environment.pusher.key, {
                cluster: environment.pusher.cluster,
                forceTLS: environment.pusher.useTLS,
                authEndpoint: `${environment.apiUrl}/broadcasting/auth`,
                auth: {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                },
                // Enable multiple transport fallbacks
                enabledTransports: ['ws', 'wss', 'xhr_polling', 'xhr_streaming'],
                // Add connection timeout and activity timeout
                activityTimeout: 120000, // 2 minutes
                pongTimeout: 30000, // 30 seconds
            });

            // Connection state management
            this.pusher.connection.bind('state_change', (states: any) => {

                switch (states.current) {
                    case 'connected':
                        this.onConnected();
                        break;
                    case 'disconnected':
                        this.onDisconnected();
                        break;
                    case 'failed':
                        this.onConnectionFailed();
                        break;
                    case 'unavailable':
                        this.onConnectionUnavailable();
                        break;
                }
            });

            this.pusher.connection.bind('error', (err: any) => {
                console.error('Pusher connection error:', err);
                this.handleConnectionError(err);
            });

        } catch (error) {
            console.error('Failed to initialize Pusher:', error);
            this.scheduleReconnect();
        }
    }

    private onConnected() {
        this.reconnectAttempts = 0;
        this.isReconnecting = false;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        // Resubscribe to all active chat channels
        this.activeChannels.forEach((_, conversationId) => {
            this.subscribeToConversation(conversationId);
        });

        // Resubscribe to all desired assignment channels
        this.assignmentChannels.clear();
        this.desiredAssignmentCustomerIds.forEach(customerId => {
            this.subscribeToAssignmentChannel(customerId);
        });
    }

    private onDisconnected() {
        if (!this.isReconnecting) {
            this.scheduleReconnect();
        }
    }

    private onConnectionFailed() {
        console.error('Pusher connection failed');
        this.scheduleReconnect();
    }

    private onConnectionUnavailable() {
        console.error('Pusher connection unavailable');
        this.scheduleReconnect();
    }

    private handleConnectionError(error: any) {
        console.error('Pusher connection error details:', error);

        // Check if it's a WebSocket error
        if (error.type === 'WebSocketError') {
            console.log('WebSocket error detected, attempting fallback transport');
            // Pusher will automatically try fallback transports
        }

        this.scheduleReconnect();
    }

    private scheduleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error(`Max reconnection attempts (${this.maxReconnectAttempts}) reached. Stopping reconnection attempts.`);
            return;
        }

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
            this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
            this.maxReconnectDelay
        );
          this.reconnectTimer = setTimeout(() => {
            this.reconnectAttempts++;
            this.reconnect();
        }, delay);
    }

    private reconnect() {
        if (this.isReconnecting) {
            return; // Already reconnecting
        }

        if (!this.getAuthToken()) {
            this.isReconnecting = false;
            return;
        }

        try {
            this.isReconnecting = true;
            console.log(`Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

            if (this.pusher) {
                this.pusher.disconnect();
            }

            // Small delay before reinitializing
            setTimeout(() => {
                this.initializePusher();
            }, 1000);

        } catch (error) {
            console.error('Failed to reconnect:', error);
            this.isReconnecting = false;
            this.scheduleReconnect();
        }
    }

    subscribeToConversation(conversationId: number): void {
        try {
            if (!this.getAuthToken()) {
                return;
            }

            if (!this.ensureInitialized()) {
                return;
            }

            // Check if already subscribed to this conversation
            if (this.activeChannels.has(conversationId)) {
                console.log(`Already subscribed to conversation.${conversationId}`);
                return;
            }

            // Check if Pusher is connected
            if (!this.pusher || this.pusher.connection.state !== 'connected') {
                console.log(`Pusher not connected, queuing subscription for conversation.${conversationId}`);
                // The subscription will be attempted when connection is established
                return;
            }

            const channelName = `conversation.${conversationId}`;
            const channel = this.pusher.subscribe(channelName);

            channel.bind('pusher:subscription_succeeded', () => {
                console.log(`[PusherService] Successfully subscribed to ${channelName}`);
            });

            channel.bind('new.message', (data: { messages: MessageModel, conversation_id: number }) => {
                if (data.messages) {
                    // Check if we've already processed this message
                    if (this.processedMessageIds.has(data.messages.id)) {
                        return;
                    }

                    // First get auth state to check current user
                    this.store.select(state => state.auth).pipe(
                        take(1),
                        map(authState => {
                            const currentUserId = authState.customer?.id;
                            data.messages.isMine = currentUserId === data.messages.sender?.id;
                            return data.messages;
                        })
                    ).subscribe({
                        next: (message) => {
                            // Add message ID to processed set
                            this.processedMessageIds.add(message.id);

                            // Clean up old message IDs (optional, to prevent memory growth)
                            if (this.processedMessageIds.size > 1000) {
                                const idsArray = Array.from(this.processedMessageIds);
                                this.processedMessageIds = new Set(idsArray.slice(-500));
                            }
                            },
                        error: (error) => {
                            console.error('[PusherService] Error processing new message:', error);
                        }
                    });
                }
            });

            channel.bind('pusher:subscription_error', (error: any) => {
                console.error(`[PusherService] Subscription error for ${channelName}:`, error);
                // Remove from active channels on error
                this.activeChannels.delete(conversationId);

                // Retry subscription after a delay
                setTimeout(() => {
                    this.subscribeToConversation(conversationId);
                }, 5000);
            });

            this.activeChannels.set(conversationId, channel);
        } catch (error) {
            console.error('Error in subscribeToConversation:', error);
            // Remove from active channels on error
            this.activeChannels.delete(conversationId);
            setTimeout(() => this.subscribeToConversation(conversationId), 5000);
        }
    }

    // Reset reconnection attempts (useful when user manually triggers reconnection)
    public resetReconnectionAttempts() {
        this.reconnectAttempts = 0;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    // Check connection status
    public getConnectionState(): string {
        return this.pusher ? this.pusher.connection.state : 'uninitialized';
    }

    // Manual reconnection method
    public forceReconnect() {
        this.resetReconnectionAttempts();
        this.reconnect();
    }

    // Add method to unsubscribe from a conversation
    unsubscribeFromConversation(conversationId: number): void {
        const channel = this.activeChannels.get(conversationId);
        if (channel) {
            channel.unsubscribe();
            this.activeChannels.delete(conversationId);

        }
    }

    subscribeToAssignmentChannel(customerId: number): void {
        if (!customerId) return;

        // Always record intent so onConnected() can retry if not yet connected
        this.desiredAssignmentCustomerIds.add(customerId);

        if (this.assignmentChannels.has(customerId)) return;
        if (!this.ensureInitialized()) return;
        if (!this.pusher || this.pusher.connection.state !== 'connected') return;

        const channelName = `customer.${customerId}.assignments`;
        const channel = this.pusher.subscribe(channelName);

        channel.bind('assignment.updated', (data: AssignmentUpdateEvent) => {
            this.assignmentUpdateSubject.next(data);
        });

        channel.bind('pusher:subscription_error', (error: any) => {
            console.error(`[PusherService] Assignment channel subscription error for ${channelName}:`, error);
            this.assignmentChannels.delete(customerId);
        });

        this.assignmentChannels.set(customerId, channel);
    }

    unsubscribeFromAssignmentChannel(customerId: number): void {
        this.desiredAssignmentCustomerIds.delete(customerId);
        const channel = this.assignmentChannels.get(customerId);
        if (channel) {
            channel.unbind_all();
            channel.unsubscribe();
            this.assignmentChannels.delete(customerId);
        }
    }

    // Add method to unsubscribe from all conversations
    unsubscribeFromAllConversations(): void {
        this.activeChannels.forEach((channel, conversationId) => {
            channel.unsubscribe();

        });
        this.activeChannels.clear();
        this.processedMessageIds.clear(); // Clear processed message IDs when unsubscribing
    }

    disconnect(): void {
        // Clear reconnection timer
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.activeChannels.size > 0) {
            try {
                this.activeChannels.forEach((channel, conversationId) => {
                    this.unsubscribeFromConversation(conversationId);
                });
            } catch (error) {
                console.error('Error unsubscribing from channels:', error);
            }
        }

        if (this.pusher) {
            try {
                this.pusher.disconnect();
            } catch (error) {
                console.error('Error disconnecting from Pusher:', error);
            }
        }
    }
}
